import { useLayoutEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import { SkeletonBlurb } from 'components/Containers';
import ActivityList from 'components/patient/blurbs/activities/ActivityList';
import NewActivity from 'components/patient/blurbs/NewActivity';
import PatientClassifications from 'components/patient/blurbs/patientOverview/PatientClassifications';
import columns from 'components/patient/filesTab/tableSetup';
import AcceptedPlan from 'components/referral/blurbs/AcceptedPlan';
import ExpectedDischarge from 'components/referral/blurbs/ExpectedDischarge';
import PatientContactInformation from 'components/referral/blurbs/PatientContactInformation';
import { ReferralBanner } from 'components/referral/ReferralBanner';
import ReferralSummaryLauncher from 'components/referral/ReferralSummaryLauncher';
import ReferralSummarySlideout from 'components/referral/summary/Slideout';
import AccessGuard from 'components/shared/AccessGuard';
import Foldout from 'components/shared/Foldout';
import ControlledTable from 'components/shared/table/ControlledTable';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import { useReferral } from 'context/review/referral';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useIsMobile from 'hooks/useIsMobile';
import usePagination from 'hooks/usePagination';
import { useReferralId } from 'hooks/useReferralId';
import { simpleDate } from 'lib/date';
import Attachment from 'models/Attachment';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import { createReviewActivity } from 'services/api/activity';
import { indexAttachments } from 'services/api/attachments';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { showEpisode } from 'services/api/episode';
import { episodeQueryKeys } from 'services/api/episode';
import { serializeSort } from 'stores/patientFiltersStore';
import colors from 'styles/theme/colors';
import { BodySmall, H1 } from 'styles/typography';
import FolderIcon from 'svg/FolderIcon';

export default function ReferralSlideout() {
  const referralId = useReferralId();
  const { referral, referralLoading } = useReferral();
  const isMobile = useIsMobile();
  const { profile } = useProfile();
  const invalidateQueries = useInvalidateQueries();

  const [localSort, setLocalSort] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [foldoutOpen, setFoldoutOpen] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState<string>('referral');

  const name = referral?.episode?.patient?.name;
  const externalId = referral?.episode?.externalId;
  const dob = referral?.episode?.patient?.dateOfBirth;
  const locationEpisode = referral?.locationEpisode;
  const healthPlanClassification = locationEpisode?.planTypeClassification?.parentClassification;

  const pagination = usePagination();

  const queryParams = useMemo(() => {
    return {
      reviewId: referral?.id,
      ...pagination.paging,
      sortBy: serializeSort(localSort),
      pageSize: 25,
      archived: false,
      docType: 'All',
      include: 'createdByUser,blob,fileDeletedByClient,createdByGroup',
    };
  }, [referral, pagination.paging, localSort]);

  const { data: attachments, isPending } = useQuery({
    queryKey: attachmentsQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexAttachments(queryParams, signal),
    enabled: !!queryParams.reviewId && currentTab === 'files',
  });

  const { data: attachmentsCount } = useQuery({
    queryKey: attachmentsQueryKeys.count({ reviewId: referral?.id }),
    queryFn: ({ signal }) => indexAttachments({ reviewId: referral?.id, pageSize: 1, page: 1 }, signal),
    select: (data) => data?.meta.totalRecords,
    enabled: !!queryParams.reviewId,
  });

  // Try fetch the episode, if it's not a 404, render the link.
  const { data: episodeResponse, isRefetchError } = useQuery({
    queryKey: episodeQueryKeys.show({ id: referral?.episode?.id }),
    queryFn: () => showEpisode({ id: referral?.episode?.id ?? '' }),
    enabled: !!referral?.episode?.id,
  });

  const showPatientStoryLink = !!episodeResponse && !isRefetchError;

  const tabs = useMemo(() => {
    return [
      { label: 'Referral', value: 'referral' },
      { label: `Files (${attachmentsCount ?? '-'})`, value: 'files' },
    ];
  }, [attachmentsCount]);

  const handleTabClick = (tab: TabType) => {
    if (tab.value === 'files') {
      invalidateQueries({ attachmentsCount: true });
    }

    setCurrentTab(tab.value);
  };

  useLayoutEffect(() => {
    setCurrentTab('referral');
  }, [referralId]);

  useLayoutEffect(() => {
    if (referralId) {
      setFoldoutOpen(true);
    } else {
      setFoldoutOpen(false);
    }
  }, [referralId]);

  const handleClose = () => {
    invalidateQueries({ referrals: true });
    navigate('/referrals');
  };

  const showReferralActivityInput =
    referral?.episode &&
    !!locationEpisode &&
    locationEpisode.active &&
    !locationEpisode.archived &&
    !referral?.episode.archived &&
    referral?.committed;

  const showAcceptedPlan =
    healthPlanClassification && referral?.group?.hasHealthPlanClassification(healthPlanClassification.id);

  const isReferralSummarized = !!referral?.summary;
  const referralSummaryFileCount = referral?.attachments.length ?? 0;

  const isEligibleForSummary = !!referralSummaryFileCount && profile.referralSummarizationEnabled;

  const shouldOpenSummarySlideout = isEligibleForSummary && pathname.includes('/summary');
  const [isNestedSlideoutOpen, setIsNestedSlideoutOpen] = useState(false);

  useLayoutEffect(() => {
    if (shouldOpenSummarySlideout) {
      setIsNestedSlideoutOpen(true);
    } else {
      setIsNestedSlideoutOpen(false);
    }
  }, [shouldOpenSummarySlideout]);

  const handleOpenNestedSlideout = () => {
    if (referral) {
      navigate(`/referrals/${referral.id}/summary`);
      setIsNestedSlideoutOpen(true);
    }
  };

  const handleClosedNestedSlideout = () => {
    if (referral) {
      navigate(`/referrals/${referral.id}`);
      setIsNestedSlideoutOpen(false);
    }
  };

  return (
    <>
      <Foldout open={foldoutOpen} width='min(100%, 1200px)' onBackdropClick={handleClose}>
        <Header>
          <FilterFoldoutCloseIcon onClick={handleClose}>&times;</FilterFoldoutCloseIcon>
          <TopContent id='top-content'>
            <TopContentRow>
              <ContentColumn>
                <ContentRow id='content-row'>
                  <InnerContentRow id='inner-content-row'>
                    {!name || referralLoading ? (
                      <>
                        <SkeletonBlank $width={200} />
                        <SkeletonBlank $width={80} />
                        <SkeletonBlank $width={100} />
                      </>
                    ) : (
                      <>
                        <StyledPatientHeader id='styled-patient-header'>{name}</StyledPatientHeader>
                        {!isMobile && profile.canActAsManager && externalId && <Dob>ID: {externalId}</Dob>}
                        {!isMobile && dob && <Dob>DOB: {simpleDate(dob)}</Dob>}
                        {showPatientStoryLink && (
                          <PatientStoryLink onClick={() => navigate(`/patients/${referral?.episode?.id}`)} role='link'>
                            Go to patient story
                          </PatientStoryLink>
                        )}
                      </>
                    )}
                  </InnerContentRow>
                </ContentRow>
                <ContentRow>
                  {locationEpisode && <PatientClassifications locationEpisode={locationEpisode} />}
                </ContentRow>
              </ContentColumn>
            </TopContentRow>
          </TopContent>
          <BottomContent>
            <Tabs
              tabs={tabs}
              onTabClick={handleTabClick}
              selectedTab={tabs.find((tab) => tab.value === currentTab) ?? tabs[0]}
            />
          </BottomContent>
        </Header>
        {currentTab === 'referral' && !referralLoading && (
          <>
            <Container>
              {isEligibleForSummary && (
                <AccessGuard accessor={PRODUCT_ACCESSORS.referralSummarizationEnabled}>
                  <ReferralSummaryLauncher
                    fileCount={referralSummaryFileCount}
                    isSummarized={isReferralSummarized}
                    onLaunch={handleOpenNestedSlideout}
                  />
                </AccessGuard>
              )}
              <ReferralBanner
                review={referral}
                profile={profile}
                episode={referral?.episode}
                locationEpisode={locationEpisode}
              />
              {showAcceptedPlan && (
                <AcceptedPlan planName={healthPlanClassification.name} groupName={referral?.group?.name ?? ''} />
              )}
              <ExpectedDischarge
                showEdit={false}
                date={referral?.data.expectedDischargeDate ?? ''}
                setShowDeterminantsEdit={() => {}}
              />
              <PatientContactInformation
                address={{
                  address1: referral?.data.address1 ?? '',
                  address2: referral?.data.address2 ?? '',
                  city: referral?.data.city ?? '',
                  state: referral?.data.state ?? '',
                  zip: referral?.data.zip ?? '',
                }}
                phone={referral?.data.phoneNumber ?? ''}
                showEdit={false}
                setShowDeterminantsEdit={noop}
              />
              {showReferralActivityInput && (
                <AccessGuard
                  accessor={PERMISSIONS.activityNoteCreate}
                  locationEpisode={locationEpisode}
                  episode={referral?.episode}>
                  <NewActivity
                    canElevate={false}
                    episode={referral?.episode}
                    locationEpisode={locationEpisode}
                    taggableUserProps={{ onReview: referral?.id }}
                    mutationFn={createReviewActivity}
                    newNoteProps={{ reviewId: referral?.id }}
                  />
                </AccessGuard>
              )}
              <ActivityList
                episode={referral?.episode}
                profile={profile}
                locationEpisode={locationEpisode}
                reviewId={referral?.id}
                activitiesParameters={{ reviewId: referral?.id, viewOnly: true }}
              />
            </Container>
          </>
        )}
        {currentTab === 'files' && (
          <Container>
            {attachmentsCount ? (
              <ControlledTable<Attachment>
                data={attachments?.data ?? []}
                columns={columns(false)}
                pageCount={attachments?.meta.totalPages || 1}
                loading={isPending}
                pagination={pagination}
                sorting={{ sortBy: localSort, setSortBy: setLocalSort }}
                state={{ columnVisibility: { createdAt: !isMobile, createdByUser: !isMobile, docType: !isMobile } }}
              />
            ) : (
              <EmptyStateContainer>
                <IconContainer>
                  <FolderIcon width={32} height={32} color={colors.primaryBlue} />
                </IconContainer>
                <EmptyStateTitle>No Uploaded Files</EmptyStateTitle>
                <EmptyStateContent>All files uploaded for the referral will be listed here.</EmptyStateContent>
              </EmptyStateContainer>
            )}
          </Container>
        )}
      </Foldout>
      <ReferralSummarySlideout isNested open={isNestedSlideoutOpen} onClose={handleClosedNestedSlideout} />
    </>
  );
}

const MAX_CONTENT_WIDTH = 1072;

const StyledPatientHeader = styled(H1)`
  font-weight: var(--font-weight-extra-bold);
  font-size: 20px;
  @media ${({ theme }) => theme.devices.desktop} {
    font-size: 24px;
  }
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Dob = styled(BodySmall)`
  color: var(--black-75);
  padding-top: 4px;
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  flex-shrink: 0;
`;

const Header = styled.div`
  background-color: white;
  border-bottom: 1px solid ${({ theme }) => theme.colors.black10};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TopContent = styled.div`
  padding-left: 24px;
  padding-top: 16px;
  padding-bottom: 16px;
  max-width: 1152px;

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 24px 24px 24px 56px;
  }
`;

const TopContentRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  @media ${({ theme }) => theme.devices.desktop} {
    flex-direction: row;
  }
`;

const BottomContent = styled.div`
  padding-left: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.black10};

  @media ${({ theme }) => theme.devices.desktop} {
    padding: 0px 24px 0px 56px;
  }
`;

const InnerContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 4px 0px;
`;

const ContentRow = styled.div`
  min-width: 0;
  gap: 6px;
  overflow: hidden;
  flex-direction: column;
  display: flex;
  max-width: ${MAX_CONTENT_WIDTH}px;

  @media ${({ theme }) => theme.devices.desktop} {
    flex-direction: row;
  }

  .icon {
    flex-shrink: 0;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const SkeletonBlank = styled(SkeletonBlurb)<{ $width: number }>`
  border-radius: 17px;
  height: 17px;
  width: ${({ $width }) => $width + 'px'};
`;

const FilterFoldoutCloseIcon = styled.div`
  color: ${colors.black75};
  cursor: pointer;
  font-size: 24px;
  position: absolute;
  right: 24px;
  top: 16px;
`;

const ResponsiveColumn = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px 56px;
`;

const Container = styled(ResponsiveColumn)`
  width: 100%;
  overflow-y: auto;
  height: calc(100vh - 200px);
  background-color: ${({ theme }) => theme.colors.black05};
`;

const PatientStoryLink = styled.p`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-size: 12px;
  margin: 0;
  cursor: pointer;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background-color: #ffffff;
  margin-bottom: 25px;
  padding: 40px;
`;

const IconContainer = styled.div`
  margin-bottom: 16px;
`;

const EmptyStateContent = styled.div`
  color: rgba(15, 18, 38, 0.75);
  font-size: 14px;
`;

const EmptyStateTitle = styled.div`
  font-color: rgba(15, 18, 38, 0.75);
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;
  margin-bottom: 8px;
`;
